import type { HelpTextProps } from "@react-types/shared/src/inputs";
import type { FieldsetHTMLAttributes, ForwardedRef, HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { useField } from "react-aria";
import { Text } from "react-aria-components";

import { cn } from "../../utils";
import { NcGroup } from "../nc-group";

type FieldVariants = "horizontal" | "vertical";

export interface FieldProps extends HelpTextProps {
  label: string;
  labelNode?: ReactNode;
  name: string;
  variant?: FieldVariants;
}

interface FieldsetProps
  extends FieldProps,
    Omit<FieldsetHTMLAttributes<HTMLFieldSetElement>, "name"> {
  children: ReactNode;
  errorMessage?: ReactNode | string;
}

/**
 * @deprecated Use src/components/nc-fieldset instead.
 */
export const NcFieldset = forwardRef(
  (
    { errorMessage, children, labelNode, className, ...props }: FieldsetProps,
    ref: ForwardedRef<HTMLFieldSetElement>
  ) => {
    const { labelProps, fieldProps, errorMessageProps, descriptionProps } = useField(props);
    return (
      <NcGroup data-nc="NcFieldset">
        <fieldset
          {...fieldProps}
          {...props}
          className={cn(
            // Border, radius, padding, background
            "border-ui bg-ui-light rounded-md border px-4 pt-2 pb-4",
            // Disabled
            "disabled:bg-disabled-light disabled:text-disabled disabled:opacity-80",
            // Invalid
            "aria-invalid:true:border-danger",
            // Focus within
            "focus-within:border-active",
            "group",
            className
          )}
          ref={ref}
          aria-invalid={!!errorMessage}
        >
          <legend
            {...(labelProps as HTMLAttributes<HTMLLegendElement>)}
            className={cn(
              // Padding, margin, background, color, font
              "bg-main text-light mb-1 rounded px-2 py-1 font-bold",
              // Invalid
              "group-aria-invalid:true:bg-danger",
              // Disabled
              "group-disabled:bg-disabled group-disabled:text-disabled-light"
            )}
          >
            {labelNode || props.label}
          </legend>
          <div {...descriptionProps} className="not-empty:mb-3">
            <Text slot="description">{props.description}</Text>
            {errorMessage && (
              <div {...errorMessageProps} className="text-danger-active">
                {errorMessage}
              </div>
            )}
          </div>
          <div className="grid gap-2">{children}</div>
        </fieldset>
      </NcGroup>
    );
  }
);
